import { FormEvent, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { getCurrentStore, updateCurrentStore } from '@/lib/api/stores.js';
import {
  DEFAULT_FEATURE_FLAGS,
  mergeFeatureFlags,
  type StoreFeatureFlags,
} from '@/lib/permissions.js';
import {
  DEFAULT_SETTINGS_SECTIONS,
  SETTINGS_SECTION_META,
  mergeSettingsSections,
  type SettingsSectionKey,
} from '@/lib/settings-access.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { prettyLabel } from '@/lib/format.js';
import { useSettingsAccess } from '@/hooks/useSettingsAccess.js';const ROLE_OPTIONS = [
  { id: 'platform_admin', label: 'Platform Admin' },
  { id: 'tenant_admin', label: 'Tenant Admin' },
  { id: 'store_manager', label: 'Store Manager' },
  { id: 'technician', label: 'Technician' },
  { id: 'front_desk', label: 'Front Desk' },
  { id: 'read_only', label: 'Read Only' },
] as const;

const MIN_ROLE_OPTIONS = ROLE_OPTIONS.filter((r) => r.id !== 'platform_admin');

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-950">{title}</h3>
      {description ? <p className="mt-1 text-sm text-slate-500">{description}</p> : null}
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

function RoleChecklist({
  label,
  roles,
  selected,
  onChange,
}: {
  label: string;
  roles: typeof ROLE_OPTIONS;
  selected: string[];
  onChange: (roles: string[]) => void;
}) {
  function toggle(roleId: string) {
    onChange(
      selected.includes(roleId) ? selected.filter((r) => r !== roleId) : [...selected, roleId],
    );
  }

  return (
    <div>
      <p className="mb-2 text-sm font-medium text-slate-700">{label}</p>
      <div className="space-y-2">
        {roles.map((role) => (
          <label
            key={role.id}
            className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium"
          >
            <span>{role.label}</span>
            <input
              type="checkbox"
              checked={selected.includes(role.id)}
              onChange={() => toggle(role.id)}
            />
          </label>
        ))}
      </div>
    </div>
  );
}

function MinRoleSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-sm font-medium text-slate-700">
      {label}
      <select
        className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {MIN_ROLE_OPTIONS.map((role) => (
          <option key={role.id} value={role.id}>
            {role.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function PermissionsSettingsPage() {
  const queryClient = useQueryClient();
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const [flags, setFlags] = useState<StoreFeatureFlags>(DEFAULT_FEATURE_FLAGS);
  const [expandedSection, setExpandedSection] = useState<SettingsSectionKey | null>(null);
  const { canView, isLoading: accessLoading } = useSettingsAccess('permissions');

  useEffect(() => {
    if (storeQuery.data) {
      setFlags(mergeFeatureFlags(storeQuery.data.featureFlags ?? {}));
    }
  }, [storeQuery.data]);

  const mutation = useMutation({
    mutationFn: () => updateCurrentStore({ featureFlags: flags as Record<string, unknown> }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Permissions saved');
    },
    onError: (error) => {
      toastError(parseApiError(error).message);
    },
  });

  const sections = useMemo(() => mergeSettingsSections(flags), [flags]);

  const groupedMeta = useMemo(() => {
    const groups = new Map<string, typeof SETTINGS_SECTION_META>();
    for (const item of SETTINGS_SECTION_META) {
      const list = groups.get(item.group) ?? [];
      list.push(item);
      groups.set(item.group, list);
    }
    return [...groups.entries()];
  }, []);

  if (!accessLoading && !canView) {
    return <Navigate to="/settings/store" replace />;
  }

  function patch(partial: Partial<StoreFeatureFlags>) {
    setFlags((current) => ({ ...current, ...partial }));
  }

  function patchSection(key: SettingsSectionKey, partial: { viewRoles?: string[]; editRoles?: string[] }) {
    setFlags((current) => {
      const merged = mergeSettingsSections(current);
      const next = {
        ...merged[key],
        ...partial,
      };
      return {
        ...current,
        settings_sections: {
          ...(current.settings_sections ?? {}),
          ...Object.fromEntries(
            (Object.keys(DEFAULT_SETTINGS_SECTIONS) as SettingsSectionKey[]).map((k) => [
              k,
              k === key ? next : merged[k],
            ]),
          ),
        },
      };
    });
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <div>
        <h2 className="text-xl font-bold text-slate-950">Permissions</h2>
        <p className="mt-1 text-sm text-slate-500">
          Control what each role can do at this store. Changes apply immediately for all staff.
        </p>
      </div>

      <Section title="Discounts" description="Limits on line-item and cart discounts.">
        <label className="block text-sm font-medium text-slate-700">
          Maximum discount without approval (%)
          <input
            type="number"
            min={0}
            max={100}
            className="mt-1 w-full max-w-xs rounded-xl border border-slate-300 px-3 py-2"
            value={flags.discount_max_percent ?? 100}
            onChange={(e) => patch({ discount_max_percent: Number(e.target.value) })}
          />
          <span className="mt-1 block text-xs font-normal text-slate-500">
            Above this requires a manager PIN override. Managers and tenant admins are exempt.
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={flags.discount_require_reason ?? true}
            onChange={(e) => patch({ discount_require_reason: e.target.checked })}
          />
          Require reason for discounts
        </label>
      </Section>

      <Section title="Voids & Refunds">
        <RoleChecklist
          label="Who can void without manager override"
          roles={ROLE_OPTIONS}
          selected={flags.void_allowed_roles ?? []}
          onChange={(roles) => patch({ void_allowed_roles: roles })}
        />
        <p className="-mt-2 text-xs text-slate-500">
          Staff below this level will see a manager override PIN prompt.
        </p>
        <RoleChecklist
          label="Who can issue refunds without manager override"
          roles={ROLE_OPTIONS}
          selected={flags.refund_allowed_roles ?? []}
          onChange={(roles) => patch({ refund_allowed_roles: roles })}
        />
        <p className="-mt-2 text-xs text-slate-500">
          Staff below this level will see a manager override PIN prompt.
        </p>
      </Section>

      <Section title="Pricing">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={flags.price_override_allowed ?? true}
            onChange={(e) => patch({ price_override_allowed: e.target.checked })}
          />
          Allow price overrides
        </label>
        <MinRoleSelect
          label="Minimum role for price overrides"
          value={flags.price_override_min_role ?? 'front_desk'}
          onChange={(value) => patch({ price_override_min_role: value })}
        />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={flags.price_override_require_pin ?? true}
            onChange={(e) => patch({ price_override_require_pin: e.target.checked })}
          />
          Require manager override for price changes
        </label>
        <span className="-mt-1 block text-xs text-slate-500">
          When enabled, non-managers must get a manager PIN to change a quoted price.
        </span>
        <RoleChecklist
          label="Who can view the pricing matrix"
          roles={ROLE_OPTIONS}
          selected={
            flags.pricing_matrix_view_roles ??
            flags.pricing_matrix_roles ??
            DEFAULT_FEATURE_FLAGS.pricing_matrix_view_roles ??
            []
          }
          onChange={(roles) => patch({ pricing_matrix_view_roles: roles })}
        />
        <RoleChecklist
          label="Who can edit prices in the pricing matrix"
          roles={ROLE_OPTIONS}
          selected={
            flags.pricing_matrix_edit_roles ??
            flags.pricing_matrix_roles ??
            DEFAULT_FEATURE_FLAGS.pricing_matrix_edit_roles ??
            []
          }
          onChange={(roles) => patch({ pricing_matrix_edit_roles: roles })}
        />
      </Section>

      <Section title="Cost & Profit Visibility">
        <RoleChecklist
          label="Roles that can view cost prices"
          roles={ROLE_OPTIONS}
          selected={flags.show_cost_prices_roles ?? []}
          onChange={(roles) => patch({ show_cost_prices_roles: roles })}
        />
        <RoleChecklist
          label="Roles that can view profit margins"
          roles={ROLE_OPTIONS}
          selected={flags.show_profit_margin_roles ?? []}
          onChange={(roles) => patch({ show_profit_margin_roles: roles })}
        />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={flags.show_supplier_costs ?? false}
            onChange={(e) => patch({ show_supplier_costs: e.target.checked })}
          />
          Show supplier costs on purchase orders
        </label>
      </Section>

      <Section title="Reports & Dashboard">
        <MinRoleSelect
          label="Minimum role to view the home dashboard"
          value={flags.dashboard_min_role ?? 'store_manager'}
          onChange={(value) => patch({ dashboard_min_role: value })}
        />
        <p className="text-xs text-slate-500">
          Default is Store Manager (includes Tenant Admin / Platform Admin). Lower this to let
          front desk or technicians see the dashboard.
        </p>
        <MinRoleSelect
          label="Minimum role to view reports"
          value={flags.reports_min_role ?? 'store_manager'}
          onChange={(value) => patch({ reports_min_role: value })}
        />
        <MinRoleSelect
          label="Minimum role for financial reports"
          value={flags.financial_reports_min_role ?? 'tenant_admin'}
          onChange={(value) => patch({ financial_reports_min_role: value })}
        />
      </Section>

      <Section title="Customers">
        <RoleChecklist
          label="Roles that can edit customers"
          roles={ROLE_OPTIONS}
          selected={flags.can_edit_customer_roles ?? []}
          onChange={(roles) => patch({ can_edit_customer_roles: roles })}
        />
        <RoleChecklist
          label="Roles that can delete customers"
          roles={ROLE_OPTIONS}
          selected={flags.can_delete_customer_roles ?? []}
          onChange={(roles) => patch({ can_delete_customer_roles: roles })}
        />
        <RoleChecklist
          label="Roles that can view archived customers"
          roles={ROLE_OPTIONS}
          selected={flags.customer_archive_view_roles ?? []}
          onChange={(roles) => patch({ customer_archive_view_roles: roles })}
        />
      </Section>

      <Section title="Inventory & Purchasing">
        <MinRoleSelect
          label="Minimum role for inventory adjustments"
          value={flags.inventory_adjustment_min_role ?? 'store_manager'}
          onChange={(value) => patch({ inventory_adjustment_min_role: value })}
        />
        <RoleChecklist
          label="Roles that can receive purchase orders"
          roles={ROLE_OPTIONS}
          selected={flags.can_receive_po_roles ?? []}
          onChange={(roles) => patch({ can_receive_po_roles: roles })}
        />
        <RoleChecklist
          label="Roles that can view archived inventory"
          roles={ROLE_OPTIONS}
          selected={flags.inventory_archive_view_roles ?? []}
          onChange={(roles) => patch({ inventory_archive_view_roles: roles })}
        />
      </Section>

      <Section title="Cash Drawer">
        <MinRoleSelect
          label="Minimum role to open cash sessions"
          value={flags.cash_session_min_role ?? 'front_desk'}
          onChange={(value) => patch({ cash_session_min_role: value })}
        />
        <RoleChecklist
          label="Roles that can view cash reports"
          roles={ROLE_OPTIONS}
          selected={flags.can_view_cash_report ?? []}
          onChange={(roles) => patch({ can_view_cash_report: roles })}
        />
      </Section>

      <Section title="Ticket Operations">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={flags.can_change_promised_date ?? true}
            onChange={(e) => patch({ can_change_promised_date: e.target.checked })}
          />
          Allow staff to change promised date
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={flags.can_waive_deposit ?? false}
            onChange={(e) => patch({ can_waive_deposit: e.target.checked })}
          />
          Allow deposit waivers
        </label>
        <MinRoleSelect
          label="Minimum role to waive deposits"
          value={flags.deposit_waiver_min_role ?? 'store_manager'}
          onChange={(value) => patch({ deposit_waiver_min_role: value })}
        />
        <p className="-mt-2 text-xs text-slate-500">
          Staff who cannot waive alone will be prompted for a manager override PIN.
        </p>
      </Section>

      <Section
        title="Settings pages"
        description="Control who can see each settings page and who can save changes. Unchecked roles will not see that page in the settings menu."
      >
        <div className="space-y-3">
          {groupedMeta.map(([group, items]) => (
            <div key={group}>
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">{group}</p>
              <div className="space-y-2">
                {items.map((item) => {
                  const open = expandedSection === item.key;
                  const access = sections[item.key];
                  return (
                    <div
                      key={item.key}
                      className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                        onClick={() => setExpandedSection(open ? null : item.key)}
                      >
                        <span className="text-sm font-semibold text-slate-900">{item.label}</span>
                        <span className="flex items-center gap-2 text-xs text-slate-500">
                          <span>
                            View {access.viewRoles.length} · Edit {access.editRoles.length}
                          </span>
                          {open ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </span>
                      </button>
                      {open ? (
                        <div className="grid gap-4 border-t border-slate-200 bg-white p-4 md:grid-cols-2">
                          <RoleChecklist
                            label="Can view"
                            roles={ROLE_OPTIONS}
                            selected={access.viewRoles}
                            onChange={(roles) => {
                              const editRoles = access.editRoles.filter((r) => roles.includes(r));
                              patchSection(item.key, { viewRoles: roles, editRoles });
                            }}
                          />
                          <RoleChecklist
                            label="Can edit / save"
                            roles={ROLE_OPTIONS}
                            selected={access.editRoles}
                            onChange={(roles) => {
                              // Edit implies view
                              const viewRoles = Array.from(
                                new Set([...access.viewRoles, ...roles]),
                              );
                              patchSection(item.key, { viewRoles, editRoles: roles });
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <button
        type="submit"
        disabled={mutation.isPending || storeQuery.isLoading}
        className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-50"
      >
        {mutation.isPending ? 'Saving…' : 'Save Permissions'}
      </button>

      <p className="text-xs text-slate-400">
        Current role hierarchy: {MIN_ROLE_OPTIONS.map((r) => prettyLabel(r.id)).join(' → ')}
      </p>
    </form>
  );
}
